import { EyeOutlined, FileAddOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Space } from "antd";
import { lowerCase } from "lodash";
import { useState } from "react";
import { CustomerServiceApi } from "src/api";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "src/assets/icons";
import { Card, Table } from "src/components";
import CustomBreadcrumb from "src/components/CustomBreadcrumb/CustomBreadcrumb";
import { capitalizeFirstLetter, context, dictionary, i18n } from "src/utils";
import { CUSTOMER_SERVICE_FIELDS } from "src/utils/enums/customer-service.enum";
import DocumentUploadModal from "./DocumentUploadModal";

const { Column } = Table;

type Props = {
  contracts: Contract[];
  onCreateContract(text?: string, description?: string): void;
  onDeleteContract(id: number): void;
  onEditContract(id: number): void;
  onViewContract(id: number): void;
};

function Document({
  contracts,
  onCreateContract,
  onDeleteContract,
  onEditContract,
  onViewContract,
}: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [text, setText] = useState("");

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <DocumentUploadModal
        open={uploadOpen}
        onCancel={() => {
          setUploadOpen(false);
        }}
        loadDocument={(text: string, description: string) => {
          setText(text);
          onCreateContract(text, description);
        }}
      />
      <Modal
        title={i18n(dictionary.label.new_item, {
          context: context.male,
          item: lowerCase(i18n(dictionary.label.document)),
        })}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        okButtonProps={{ style: { display: "none" } }}
        cancelText={i18n(dictionary.action.close)}
        onCancel={handleCancel}
      >
        <Space
          direction="vertical"
          style={{ paddingLeft: "36px", marginTop: "24px" }}
        >
          <p>{i18n(dictionary.label.select_option)}:</p>
          <Space>
            <Button
              icon={<UploadOutlined />}
              type="primary"
              onClick={() => {
                setUploadOpen(true);
              }}
            >
              {i18n(dictionary.label.upload_document, {
                context: context.male,
                item: lowerCase(i18n(dictionary.label.document)),
              })}
            </Button>
            <Button
              icon={<FileAddOutlined />}
              type="primary"
              onClick={() => onCreateContract()}
            >
              {i18n(dictionary.label.manual_document, {
                context: context.male,
                item: lowerCase(i18n(dictionary.label.document)),
              })}
            </Button>
          </Space>
        </Space>
      </Modal>
      <Card
        title={
          <CustomBreadcrumb
            root={i18n(dictionary.menu.registration)}
            title={i18n(dictionary.label.document, { count: 2 })}
          />
        }
        actions={
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={showModal}
          >
            {i18n(dictionary.label.new_item, {
              context: context.male,
              item: lowerCase(i18n(dictionary.label.document)),
            })}
          </Button>
        }
      >
        <Table
          scroll={{ x: true }}
          dataSource={contracts}
        >
          <Column<Contract>
            title={capitalizeFirstLetter(i18n(dictionary.label.document))}
            dataIndex="description"
            sorter={(a, b) => a.description.localeCompare(b.description)}
          />
          <Column<Contract>
            width={20}
            title={i18n(dictionary.label.action)}
            render={(_, record) => (
              <Space direction="horizontal">
                <Button
                  type="default"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    CustomerServiceApi.update({ [CUSTOMER_SERVICE_FIELDS.AD_VIEW_EDIT_CONTRACT_CLICKED]: true });
                    onViewContract(record.id);
                  }}
                >
                  {i18n(dictionary.action.view)}
                </Button>
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => {
                    CustomerServiceApi.update({ [CUSTOMER_SERVICE_FIELDS.AD_VIEW_EDIT_CONTRACT_CLICKED]: true });
                    onEditContract(record.id);
                  }}
                >
                  {i18n(dictionary.action.edit)}
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteContract(record.id)}
                >
                  {i18n(dictionary.action.exclude)}
                </Button>
              </Space>
            )}
          />
        </Table>
      </Card>
    </>
  );
}

export default Document;
